import React, { useRef, useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { useToast } from "../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationErrors";
import api from "../../service";
import { useCallback } from "react";
import FormButton from "../../components/FormButton";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../utils/formValidations";
import clientTypesConstants from "../../constants/clientTypes";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  SubmitContainer,
  GoBackContainer,
  ActionContainer,
  BadgeContainer,
  LinkButton,
  NotificationContainer,
} from "./styles";
import SelectWithLabel from "../../components/SelectWithLabel";
import ReportYearEventFilter from "../../components/ReportYearEventFilter";
import useReportYearEventFilter from "../../hooks/useReportYearEventFilter";
import Badge from "../../components/Badge";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import Table from "../../components/Table";
import Report from "./report";
import { format } from "date-fns";
import { convertYYYYMMDDToDate } from "../../utils/date";

const ManageExhibitorsDidNotAccessEventReport = () => {
  const { token } = useSelector((state) => state.auth);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [filtered, setFiltered] = useState(false);
  const [filename, setFilename] = useState();
  const clientTypes = clientTypesConstants();

  const clearResults = useCallback(() => {
    setShowExport(false);
    setData([]);
    setFiltered(false);
  }, []);

  const {
    years,
    events,
    eventFieldDisabled,
    onChangeYear,
    resetYearEventFilter,
  } = useReportYearEventFilter({ formRef, onYearChange: clearResults });

  const schema = Yup.object().shape({
    year: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "report_filter_year_required" })),
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    status: Yup.bool().nullable(),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        await schema.validate(data, { abortEarly: false });

        api
          .patch(
            `/admin/manage-administrative/exhibitors-access-by-event`,
            {
              eventEditionCode: data.event,
              status: data.status,
              year: data.year ? Number(data.year) : null,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            setData(result.data.output);
            setShowExport(true);
          })
          .catch((err) => {
            setData([]);
            setShowExport(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            setLoading(false);
            setFiltered(true);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, schema, token]
  );

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const clearFields = () => {
    formRef.current.reset();
    resetYearEventFilter();
    formRef.current.setFieldValue("status", {
      label: intl.formatMessage({ id: "both" }),
      value: null,
    });
    clearResults();
  };

  const onChangeEvent = (event) => {
    setFilename(event?.label?.toUpperCase());
    clearResults();
  };

  const statusOptions = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: "yes" }),
        value: true,
      },
      {
        label: intl.formatMessage({ id: "no" }),
        value: false,
      },
      {
        label: intl.formatMessage({ id: "both" }),
        value: null,
      },
    ],
    [intl]
  );

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "id_number_cnpj" }),
        selector: (row) => row.accountCode,
        allowOverflow: true,
        wrap: true,
        minWidth: "145px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "company_name" }),
        selector: (row) => row.accountName,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "commercial_name" }),
        selector: (row) => row.accountAlias,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "booth" }),
        selector: (row) => row.booth,
        allowOverflow: false,
        wrap: true,
        maxWidth: "130px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "area_" }),
        selector: (row) => row.area,
        maxWidth: "110px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "contract_number" }),
        selector: (row) => row.opportunity,
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
        maxWidth: "130px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "salesforce_close_date" }),
        selector: (row) =>
          row.salesforceCloseDate
            ? format(convertYYYYMMDDToDate(row.salesforceCloseDate), "dd/MM/yyyy")
            : "-",
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
        maxWidth: "120px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "opportunity_created_at" }),
        selector: (row) =>
          row.opportunityCreatedAt
            ? format(new Date(row.opportunityCreatedAt), "dd/MM/yyyy")
            : "-",
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
        maxWidth: "120px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "opportunity_updated_at" }),
        selector: (row) =>
          row.opportunityUpdatedAt
            ? format(new Date(row.opportunityUpdatedAt), "dd/MM/yyyy")
            : "-",
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
        maxWidth: "120px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "account_telephone" }),
        selector: (row) => row.phone,
        allowOverflow: true,
        wrap: true,
        minWidth: "145px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "signin_email" }),
        selector: (row) => row.mail,
        allowOverflow: true,
        wrap: true,
        minWidth: "230px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "operational_responsible" }),
        selector: (row) => row.contact,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "operational_email" }),
        selector: (row) => row.contactMail,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "marketing_email" }),
        selector: (row) => row.marketingMail,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "operational_telephone" }),
        selector: (row) => row.contactPhone,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "salesman" }),
        selector: (row) => row.salesman,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
        sortable: true,
      },
      {
        name: intl.formatMessage({ id: "client_type" }),
        selector: (row) => row.clientType,
        cell: (row) =>
          clientTypes.filter((pt) => pt.value === row.clientType)[0]?.label,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "user_type" }),
        selector: (row) => row.userType,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "contract_type" }),
        selector: (row) => row.contractType,
        allowOverflow: true,
        wrap: true,
        minWidth: "155px",
      },
      {
        name: intl.formatMessage({ id: "informa_client_type" }),
        selector: (row) => intl.formatMessage({ id: row.informaClientType?.toLowerCase() }),
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
      },
      {
        name: intl.formatMessage({ id: "portal_access" }),
        selector: (row) =>
          row.portalAccessed
            ? intl.formatMessage({ id: "yes" })
            : intl.formatMessage({ id: "no" }),
        sortable: true,
        minWidth: "185px",
      },
      {
        name: intl.formatMessage({ id: "event_access" }),
        selector: (row) =>
          row.eventAccessed
            ? intl.formatMessage({ id: "yes" })
            : intl.formatMessage({ id: "no" }),
        sortable: true,
        minWidth: "175px",
      },
      {
        name: intl.formatMessage({ id: "first_access_email" }),
        selector: (row) => row.firstEmail,
      },
      {
        name: intl.formatMessage({ id: "credentials" }),
        selector: (row) => row.credentialCount,
      },
      {
        name: `${intl.formatMessage({ id: "associated_service_provider" })}?`,
        selector: (row) =>
          row.hasAssociatedServiceProvider
            ? intl.formatMessage({ id: "yes" })
            : intl.formatMessage({ id: "no" }),
      },
    ],
    [intl, clientTypes]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({
        id: "breadcumb.exhibitors_access_by_event_report",
      })}
    >
      <Container>
        <FormContainer>
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{
              status: {
                label: intl.formatMessage({ id: "both" }),
                value: null,
              },
            }}
          >
            <TitleContainer>
              <Title>
                <FormattedMessage id="export_exhibitors_access_by_event_report" />
              </Title>
              <GoBackContainer>
                <ActionContainer>
                  <BadgeContainer>
                    <LinkButton onClick={() => history.push("/dashboard")}>
                      <Badge
                        color="#002244"
                        text={intl.formatMessage({ id: "goback" })}
                        fontSize="12px"
                      />
                    </LinkButton>
                  </BadgeContainer>
                </ActionContainer>
              </GoBackContainer>
            </TitleContainer>
            <FieldsContainer>
              <Horizontal>
                <ReportYearEventFilter
                  years={years}
                  events={events}
                  onChangeYear={onChangeYear}
                  onChangeEvent={onChangeEvent}
                  eventFieldDisabled={eventFieldDisabled}
                  validationBlur={validationBlur}
                />
                <SelectWithLabel
                  name="status"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "event_access" })}
                  placeholder={intl.formatMessage({ id: "event_access" })}
                  labelOrientation="vertical"
                  options={statusOptions}
                />
              </Horizontal>
              <Horizontal>
                <FilterActionsContainer>
                  <SubmitContainer>
                    <FormButton
                      color="#C9C9C9"
                      type="button"
                      onClick={clearFields}
                    >
                      <FormattedMessage id="clear_fields" />
                    </FormButton>
                  </SubmitContainer>
                  <SubmitContainer>
                    <FormButton type="submit" loading={loading}>
                      <FormattedMessage id="search" />
                    </FormButton>
                  </SubmitContainer>
                  {showExport ? (
                    <SubmitContainer>
                      <FormButton type="button" loading={loading}>
                        <Report
                          filename={`${filename}_EXHIBITORS_DIDN'T_ACCESS_EVENT`}
                          data={data}
                          intl={intl}
                          clientTypes={clientTypes}
                        >
                          <FormattedMessage id="export" />
                        </Report>
                      </FormButton>
                    </SubmitContainer>
                  ) : null}
                </FilterActionsContainer>
              </Horizontal>
            </FieldsContainer>
          </Form>
        </FormContainer>
      </Container>
      {filtered && data.length > 0 ? (
        <Table
          columns={columns}
          data={data}
          title={intl.formatMessage({ id: "results" })}
          hasPagination
        />
      ) : (
        <NotificationContainer>
          <FormattedMessage
            id={
              filtered
                ? "report_filter_no_data_for_year_and_event"
                : "need_to_filter_first"
            }
          />
        </NotificationContainer>
      )}
    </AnimatedMainContainer>
  );
};

export default ManageExhibitorsDidNotAccessEventReport;
